/**
 * Mehrere angenommene Angebote an einem Projekt — wie gehören sie zusammen?
 * (Benjamin, 20.10.2026; Konzept docs/system/KONZEPT-AUFTRAGSWERT-POSITIONEN.md § 3.2)
 *
 * Der Auftragswert zählt alle angenommenen Angebote. Ist eins durch eine neue Fassung
 * ersetzt oder steckt es in einem anderen (Fall Loose: AG0221 in AG0196), darf es nicht
 * doppelt zählen — das weiß nur der Mensch. Die Frage steht so lange am Projekt, bis sie
 * beantwortet ist; danach eine leise Zeile mit „Zurücksetzen".
 */
import { fehlerText } from "@vimaana/ui";
import { useAngebotRolle } from "../../hooks/useInvoices";
import { formatEur } from "../../lib/planer";
import type { ProjektAuftragswert } from "../../lib/types";

const ROLLE_TEXT = {
  ersetzt: (n: string, zu: string) => `${n} ist durch ${zu} ersetzt`,
  enthalten: (n: string, zu: string) => `${n} ist in ${zu} enthalten`,
  zusaetzlich: (n: string, zu: string) => `${n} ist zusätzlich zu ${zu} beauftragt`,
} as const;

export default function AngebotZusammenhang({ wert }: { wert: ProjektAuftragswert | undefined }) {
  const rolle = useAngebotRolle();
  const fragen = wert?.angebote_frage || [];
  const rollen = wert?.angebote_rollen || [];
  if (!fragen.length && !rollen.length) return null;
  const eur = (n: number) => formatEur(n, { round: true });

  return (
    <div className="pk-angebote">
      {fragen.map((f) => (
        <div key={f.id} className="vg-banner is-warn pk-angebote-frage" role="group" aria-label={`Zusammenhang ${f.nummer}`}>
          <p>
            Am Projekt sind zwei Angebote angenommen: <strong>{f.haupt_nummer}</strong> ({eur(f.haupt_netto)})
            und <strong>{f.nummer}</strong> ({eur(f.netto)}). Wie gehört {f.nummer} dazu?
          </p>
          <div className="vg-banner-actions pk-angebote-knoepfe">
            <button type="button" className="vg-btn" disabled={rolle.isPending}
              onClick={() => rolle.mutate({ id: f.id, rolle: "enthalten", zu: f.haupt_nummer })}>
              In {f.haupt_nummer} enthalten
            </button>
            <button type="button" className="vg-btn" disabled={rolle.isPending}
              onClick={() => rolle.mutate({ id: f.id, rolle: "zusaetzlich", zu: f.haupt_nummer })}>
              Zusätzlich beauftragt
            </button>
            <button type="button" className="vg-btn" disabled={rolle.isPending}
              onClick={() => rolle.mutate({ id: f.haupt_id, rolle: "ersetzt", zu: f.nummer })}>
              Ersetzt {f.haupt_nummer}
            </button>
          </div>
        </div>
      ))}
      {rollen.map((r) => (
        <p key={r.id} className="pk-angebote-stand">
          {ROLLE_TEXT[r.rolle](r.nummer, r.zu)}
          {r.rolle !== "zusaetzlich" ? " — zählt nicht zum Auftragswert." : "."}{" "}
          <button type="button" className="pk-textknopf" disabled={rolle.isPending}
            onClick={() => rolle.mutate({ id: r.id, rolle: null, zu: "" })}>Zurücksetzen</button>
        </p>
      ))}
      {rolle.error && <p className="pk-fehler">{fehlerText(rolle.error)}</p>}
    </div>
  );
}
